// ── Customer Experience Platform Engine ──
// Computes portal state from existing core data without mutation.
// Read-only overlay — does NOT modify job, scheduling, or financial state.

import type { Job, JobStage } from "@/data/types";
import type { ChangeOrder } from "@/data/changeOrderTypes";
import type {
  TenantPortalBranding,
  TenantPortalConfig,
  PortalTimelineStep,
  PortalTimelineStage,
  NotificationTrigger,
  PortalNotification,
  PortalThreadMessage,
  ExperienceProfile,
  PortalFeatureKey,
  NotificationTemplate,
} from "./types";

const DEFAULT_NOTIFICATION_TEMPLATES: NotificationTemplate[] = [
  {
    trigger: "job_created",
    channels: ["email", "sms"],
    subjectTemplate: "Welcome to {companyName} — Your Project is Confirmed",
    bodyTemplate: "Hi {customerName}, your {projectType} project has been confirmed.",
    enabled: true,
  },
];

// ── Branding Resolution ──

function generateBranding(tenantId: string): TenantPortalBranding {
  return {
    tenantId,
    companyName: tenantId,
    logoUrl: null,
    primaryHue: 207, primarySaturation: 80, primaryLightness: 42,
    accentHue: 24, accentSaturation: 90, accentLightness: 52,
    supportPhone: "",
    supportEmail: "",
    websiteUrl: "",
    emailSignature: `${tenantId} Team`,
    customDomain: null,
    tagline: "Customer Portal",
  };
}

function generateConfig(tenantId: string): TenantPortalConfig {
  const features = {
    portal_enabled: true,
    messaging_enabled: false,
    documents_enabled: true,
    timeline_enabled: true,
    notifications_enabled: true,
    payments_enabled: true,
    media_enabled: false,
    team_visibility_enabled: false,
  } satisfies Record<PortalFeatureKey, boolean>;
  return {
    tenantId,
    experienceProfile: "standard",
    features,
    notificationChannels: {
      job_created: ["email", "sms"],
      appointment_scheduled: ["email", "sms"],
      install_scheduled: ["email", "sms"],
      job_started: ["sms"],
      job_completed: ["email", "sms"],
      invoice_issued: ["email"],
      payment_received: ["email"],
      delay_reschedule: ["email", "sms"],
      change_order_approved: ["email"],
      support_received: ["email"],
    },
  };
}

export function resolveBranding(tenantId: string): TenantPortalBranding | undefined {
  return generateBranding(tenantId);
}

export function resolveConfig(tenantId: string): TenantPortalConfig | undefined {
  return generateConfig(tenantId);
}

export function isFeatureEnabled(
  tenantId: string,
  feature: PortalFeatureKey
): boolean {
  const config = resolveConfig(tenantId);
  if (!config) return false;
  return config.features[feature] ?? false;
}

// ── Domain / Route Resolution ──

export function resolveTenantFromDomain(hostname: string): string | null {
  if (!hostname) return null;
  const label = hostname.split(".")[0]?.trim();
  return label || null;
}

export function resolveTenantFromSlug(slug: string): string | null {
  return slug || null;
}

// ── Timeline Computation ──

const STAGE_ORDER: Record<JobStage, number> = {
  "Sale Closed": 10,
  "Review and Validation": 20,
  "Review Complete": 25,
  "Ready for Ordering": 30,
  "Ordered": 40,
  "Permit and Order Pending": 35,
  "Ready for Scheduling": 50,
  "Scheduled Tentative": 55,
  "Scheduled Confirmed": 60,
  "Ready for Dispatch": 65,
  "Available for Contractor Assignment": 70,
  "Assigned and Dispatched": 75,
  "In Progress": 80,
  "QA Review": 90,
  "Completed": 100,
  "Issue": 85,
};

export function computePortalTimeline(job: Job): PortalTimelineStep[] {
  const order = STAGE_ORDER[job.currentStage] ?? 0;

  const steps: PortalTimelineStep[] = [
    {
      stage: "booked",
      label: "Booked",
      description: "Your project has been confirmed.",
      completed: order >= 10,
      active: order >= 10 && order < 30,
      timestamp: job.contractDocument?.approvedAt || null,
    },
    {
      stage: "scheduled",
      label: "Scheduled",
      description: job.scheduledInstallDate
        ? `Installation on ${new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}`
        : "Your installation date will be confirmed soon.",
      completed: order >= 55 && !!job.scheduledInstallDate,
      active: order >= 55 && order < 40 && !!job.scheduledInstallDate,
      timestamp: job.scheduledInstallDate || null,
    },
    {
      stage: "materials_ordered",
      label: "Materials Ordered",
      description: "All materials for your project are being prepared.",
      completed: order >= 40,
      active: order >= 30 && order < 55,
      timestamp: null,
    },
    {
      stage: "in_progress",
      label: "In Progress",
      description: "Our team is on-site working on your project.",
      completed: order >= 90,
      active: order >= 80 && order < 90,
      timestamp: null,
    },
    {
      stage: "completed",
      label: "Completed",
      description: "Your project is complete. Thank you!",
      completed: order >= 100,
      active: order === 100,
      timestamp: null,
    },
    {
      stage: "follow_up",
      label: "Follow-Up",
      description: "We're here if you need anything.",
      completed: false,
      active: false,
      timestamp: null,
    },
  ];

  return steps;
}

// ── Communication Engine ──

let _notifCounter = 0;

function interpolateTemplate(
  template: string,
  vars: Record<string, string>
): string {
  let result = template;
  Object.entries(vars).forEach(([key, value]) => {
    result = result.replace(new RegExp(`\\{${key}\\}`, "g"), value);
  });
  return result;
}

export function generatePortalNotification(
  tenantId: string,
  trigger: NotificationTrigger,
  job: Job,
  extraVars?: Record<string, string>
): PortalNotification[] {
  const branding = resolveBranding(tenantId);
  const config = resolveConfig(tenantId);
  if (!branding || !config) return [];

  if (!config.features.notifications_enabled) return [];

  const template = DEFAULT_NOTIFICATION_TEMPLATES.find(t => t.trigger === trigger);
  if (!template || !template.enabled) return [];

  const channels = config.notificationChannels[trigger] || template.channels;
  const firstName = job.customerName.split(" ")[0];

  const vars: Record<string, string> = {
    companyName: branding.companyName,
    customerName: firstName,
    projectType: job.projectType || "home improvement",
    date: job.scheduledInstallDate
      ? new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })
      : "TBD",
    ...extraVars,
  };

  return channels.map(channel => {
    _notifCounter++;
    return {
      id: `pn-${Date.now().toString(36)}-${_notifCounter}`,
      tenantId,
      jobId: job.id,
      trigger,
      channel,
      recipientName: job.customerName,
      recipientContact: channel === "email" ? (job.email || "") : (job.phone || ""),
      subject: interpolateTemplate(template.subjectTemplate, vars),
      body: interpolateTemplate(template.bodyTemplate, vars),
      sentAt: new Date().toISOString(),
      status: "sent",
    };
  });
}

// ── Unified Thread Builder ──
// Aggregates messages for a job into a single timeline.

let _threadCounter = 0;

export function buildSystemThreadMessage(
  tenantId: string,
  jobId: string,
  body: string
): PortalThreadMessage {
  _threadCounter++;
  return {
    id: `ptm-${Date.now().toString(36)}-${_threadCounter}`,
    tenantId,
    jobId,
    direction: "system",
    channel: "system",
    senderName: "System",
    body,
    timestamp: new Date().toISOString(),
    readByCustomer: false,
    readByInternal: true,
  };
}

// ── CSS Variable Builder ──

export function buildPortalCSSVariables(branding: TenantPortalBranding): Record<string, string> {
  return {
    "--portal-primary": `${branding.primaryHue} ${branding.primarySaturation}% ${branding.primaryLightness}%`,
    "--portal-primary-dark": `${branding.primaryHue} ${branding.primarySaturation}% ${Math.min(branding.primaryLightness + 10, 90)}%`,
    "--portal-accent": `${branding.accentHue} ${branding.accentSaturation}% ${branding.accentLightness}%`,
  };
}
